import { extractExposureNumber } from 'src/modules/designStudio/helpers/productSelection/productFilterUtils';
import type {
  Product,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';

export type VariationsColorGroup = { hex: string; variations: Variation[] };
export type VariationsSize = { size: string; variations: Variation[] };

/**
 * @description When the user opens the product detail view they are shown a list of colors in that product line.
 * Within this view the user can change the color and size, but each of these variations selected will still be
 * in the product line.
 *
 * The color options list can therefore be constructed once from the activeVariation.product, and memoized.
 * The list of color options will therefore not change while the user swaps the active variation.
 *
 * The colors returned must be sorted alphabetically to always render in the same order.
 *
 */
export const extractVariationColorGroupsFromProduct = (
  product: Product,
): VariationsColorGroup[] => {
  const hexMap = new Map<string, Variation[]>();

  product.variations
    .filter(v => v.color)
    .sort((a, b) => (a.color!.hex > b.color!.hex ? -1 : 1))
    .forEach(variation => {
      const { hex } = variation.color!;
      const match = hexMap.get(hex);

      if (match) {
        hexMap.set(hex, [...match, variation]);
      } else {
        hexMap.set(hex, [variation]);
      }
    });

  return Array.from(hexMap, ([key, value]) => ({
    hex: key,
    variations: value,
  }));
};

/**
 * @description when the user clicks on a color option shown in the product details view this method
 * will return the best variation from the list of possible variation in that color group,
 * we will attempt to return a variation with the same size.
 *
 * Choose the best fitting variation from a variation color group.
 *
 * const currentVariation: {size: '4'};
 * const colorGroup: {hex: '#ffffff', variations: [{size: '4', ...}, {size: '5', ...}]}
 * const bestFit = chooseBestFitVariationIndexFromVariationColorGroup(currentVariation, colorGroup) = 0
 *
 */
export const chooseBestFitVariationIndexFromVariationColorGroup = (
  currentVariation: Variation,
  selectedColorGroup: VariationsColorGroup,
): number => {
  const index = selectedColorGroup.variations.findIndex(
    v => v.facets.sizing === currentVariation.facets.sizing,
  );
  return index === -1 ? 0 : index;
};

/**
 * @description When the user optns the product detail view they are shown a list of size options.
 * These size options are of variations with the same exact color as the active color
 */
export const createSizeOptions = (
  variationColorGroup: VariationsColorGroup,
): VariationsSize[] =>
  variationColorGroup.variations
    .filter(v => !!v.facets.sizing)
    .map(v => ({
      size: v.facets.sizing!,
      variations: [v],
    }))
    .reduce(
      (unique: { size: string; variations: Variation[] }[], item) =>
        unique.find(i => i.size === item.size) ? unique : [...unique, item],
      [],
    )
    .sort((a, b) => {
      const rankOfA = extractExposureNumber(a.size) ?? -1;
      const rankOfB = extractExposureNumber(b.size) ?? -1;

      return rankOfB - rankOfA;
    })
    .reverse();
